// Paleta de comandos (Ctrl+K o /): búsqueda global en /api/buscar, recientes y acciones de navegación.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/cliente';
import type { ResultadoBusqueda } from '../api/tipos';
import { Icono, type NombreIcono } from '../components/Iconos';
import { Kbd } from '../components/Ui';
import { leerRecientes } from '../lib/recientes';
import { useUi } from './contexto';

interface Opcion {
  id: string;
  grupo: string;
  titulo: string;
  sub?: string | null;
  icono: NombreIcono;
  atajo?: string;
  hacer: () => void;
}

const GRUPO_TIPO: Record<ResultadoBusqueda['tipo'], string> = { caso: 'Casos', oferta: 'Ofertas', cliente: 'Clientes' };
const ICONO_TIPO: Record<ResultadoBusqueda['tipo'], NombreIcono> = { caso: 'tipoProyecto', oferta: 'archivo', cliente: 'edificio' };

export function Paleta({ abierta, alCerrar, puedeCrear }: { abierta: boolean; alCerrar: () => void; puedeCrear: boolean }) {
  const ref = useRef<HTMLDialogElement>(null);
  const entrada = useRef<HTMLInputElement>(null);
  const [texto, setTexto] = useState('');
  const [q, setQ] = useState('');
  const [activo, setActivo] = useState(0);
  const ui = useUi();
  const navegar = useNavigate();

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (abierta && !d.open) {
      d.showModal();
      setTexto('');
      setQ('');
      setActivo(0);
      requestAnimationFrame(() => entrada.current?.focus());
    }
    if (!abierta && d.open) d.close();
  }, [abierta]);

  useEffect(() => {
    const t = setTimeout(() => setQ(texto.trim()), 180);
    return () => clearTimeout(t);
  }, [texto]);

  const busqueda = useQuery({ queryKey: ['buscar', q], queryFn: () => api.buscar(q), enabled: abierta && q.length >= 2, staleTime: 15_000, retry: false });

  const opciones = useMemo<Opcion[]>(() => {
    const ir = (ruta: string) => () => {
      alCerrar();
      navegar(ruta);
    };
    const acciones: Opcion[] = [
      ...(puedeCrear ? [{ id: 'a-crear', grupo: 'Acciones', titulo: 'Crear caso', icono: 'mas' as NombreIcono, atajo: 'C', hacer: () => (alCerrar(), ui.abrirCrear()) }] : []),
      { id: 'a-tablero', grupo: 'Acciones', titulo: 'Ir al tablero', icono: 'tablero', atajo: 'G T', hacer: ir('/preventa/tablero') },
      { id: 'a-lista', grupo: 'Acciones', titulo: 'Ir a la lista', icono: 'lista', atajo: 'G L', hacer: ir('/preventa/lista') },
      { id: 'a-mi', grupo: 'Acciones', titulo: 'Ir a Mi trabajo', icono: 'miTrabajo', atajo: 'G M', hacer: ir('/preventa/mi-trabajo') },
      { id: 'a-apr', grupo: 'Acciones', titulo: 'Ir a Aprobaciones', icono: 'aprobaciones', hacer: ir('/preventa/aprobaciones') },
      { id: 'a-exc', grupo: 'Acciones', titulo: 'Ir a Excepciones', icono: 'excepciones', hacer: ir('/preventa/excepciones') },
      { id: 'a-ind', grupo: 'Acciones', titulo: 'Ir a Indicadores', icono: 'indicadores', hacer: ir('/preventa/indicadores') },
      { id: 'a-cat', grupo: 'Acciones', titulo: 'Ir a Catálogos', icono: 'catalogos', hacer: ir('/preventa/catalogos') },
      { id: 'a-atajos', grupo: 'Acciones', titulo: 'Ver atajos de teclado', icono: 'teclado', atajo: '?', hacer: () => (alCerrar(), ui.abrirAtajos()) },
    ];
    const t = texto.trim().toLowerCase();
    if (!t) {
      const recientes: Opcion[] = leerRecientes().map((r) => ({
        id: `r-${r.case_id}`,
        grupo: 'Vistos recientemente',
        titulo: r.tema,
        sub: [r.case_id, r.cliente].filter(Boolean).join(' · '),
        icono: 'historial',
        hacer: () => (alCerrar(), ui.abrirCaso(r.case_id)),
      }));
      return [...recientes, ...acciones];
    }
    const resultados: Opcion[] = (busqueda.data ?? []).map((r) => ({
      id: `b-${r.tipo}-${r.id}`,
      grupo: GRUPO_TIPO[r.tipo] ?? 'Resultados',
      titulo: r.titulo,
      sub: r.subtitulo,
      icono: ICONO_TIPO[r.tipo] ?? 'buscar',
      hacer: () => {
        alCerrar();
        if (r.case_id) ui.abrirCaso(r.case_id);
        else navegar(`/preventa/lista?q=${encodeURIComponent(r.titulo)}`);
      },
    }));
    const orden = ['Casos', 'Ofertas', 'Clientes'];
    resultados.sort((a, b) => orden.indexOf(a.grupo) - orden.indexOf(b.grupo));
    return [...resultados, ...acciones.filter((a) => a.titulo.toLowerCase().includes(t))];
  }, [texto, busqueda.data, puedeCrear, alCerrar, navegar, ui]);

  useEffect(() => setActivo(0), [opciones.length, q]);

  const alTecla = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActivo((i) => Math.min(opciones.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActivo((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      opciones[activo]?.hacer();
    }
  };

  useEffect(() => {
    ref.current?.querySelector(`#paleta-op-${activo}`)?.scrollIntoView({ block: 'nearest' });
  }, [activo]);

  let grupoPrevio = '';
  return (
    <dialog
      ref={ref}
      className="paleta"
      aria-label="Buscar y ejecutar comandos"
      onCancel={(e) => {
        e.preventDefault();
        alCerrar();
      }}
      onClick={(e) => e.target === ref.current && alCerrar()}
    >
      {abierta && (
        <div className="paleta__caja">
          <div className="paleta__entrada">
            <Icono nombre="buscar" tam={20} />
            <input
              ref={entrada}
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              onKeyDown={alTecla}
              placeholder="Buscar casos, ofertas, clientes, NIT o etiquetas"
              role="combobox"
              aria-expanded="true"
              aria-controls="paleta-lista"
              aria-activedescendant={opciones.length ? `paleta-op-${activo}` : undefined}
              aria-autocomplete="list"
            />
            {busqueda.isFetching && <span className="spinner spinner--mini" aria-hidden="true" />}
            <Kbd>Esc</Kbd>
          </div>
          <div className="paleta__lista" id="paleta-lista" role="listbox" aria-label="Resultados">
            {texto.trim().length === 1 && <p className="paleta__nota">Escriba al menos dos caracteres para buscar.</p>}
            {q.length >= 2 && busqueda.isSuccess && busqueda.data.length === 0 && <p className="paleta__nota">Sin resultados para «{q}».</p>}
            {busqueda.isError && <p className="paleta__nota">La búsqueda no está disponible en este momento.</p>}
            {opciones.map((o, i) => {
              const cabecera = o.grupo !== grupoPrevio ? o.grupo : null;
              grupoPrevio = o.grupo;
              return (
                <div key={o.id}>
                  {cabecera && <div className="paleta__grupo">{cabecera}</div>}
                  <div
                    id={`paleta-op-${i}`}
                    role="option"
                    aria-selected={i === activo}
                    className={`paleta__op ${i === activo ? 'paleta__op--activa' : ''}`}
                    onMouseMove={() => setActivo(i)}
                    onClick={() => o.hacer()}
                  >
                    <span className="paleta__icono">
                      <Icono nombre={o.icono} />
                    </span>
                    <span className="paleta__texto">
                      <span>{o.titulo}</span>
                      {o.sub && <small>{o.sub}</small>}
                    </span>
                    {o.atajo && <Kbd>{o.atajo}</Kbd>}
                    {i === activo && <Icono nombre="chevronDerecha" className="paleta__ir" />}
                  </div>
                </div>
              );
            })}
          </div>
          <footer className="paleta__pie">
            <span>
              <Kbd>↑</Kbd> <Kbd>↓</Kbd> moverse
            </span>
            <span>
              <Kbd>Enter</Kbd> abrir
            </span>
            <span>
              <Kbd>Ctrl</Kbd> <Kbd>K</Kbd> o <Kbd>/</Kbd> en cualquier página
            </span>
          </footer>
        </div>
      )}
    </dialog>
  );
}
